import { createAsyncThunk } from "@reduxjs/toolkit";
import { discoverAccounts, linkAccount, verifyLinkAccount, getIdentifiersType } from "../../services/api/account.api";
import type {
  AccountDiscoveryResponse,
  LinkAccountResponse,
  VerifyLinkResponse,
} from "../../services/api/api.types";
import { MOCK_ACCOUNTS } from "../../services/api/mockData";
import { getConsentList } from "../../services/api/consent.api";

import type { RootState } from "../../app/store";


import { API_DEFAULTS } from "../../services/api/config";

const USE_MOCKS = process.env.REACT_APP_USE_MOCKS === "true";



export const getIdentifiersThunk = createAsyncThunk<
  any,
  { fipId: string },
  { rejectValue: string; state: RootState }
>("account/getIdentifiers", async (payload, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const sessionId = state.auth.sessionId || "";
    const mobileNumber = state.auth.mobileNumber;

    const res = await getIdentifiersType({
      I_MOBILENUMBER: mobileNumber,
      I_BROWSER: API_DEFAULTS.BROWSER,
      I_SESSION: sessionId,
      I_USERID: mobileNumber,
      I_FIPID: payload.fipId,
      I_FIPNAME: state.fip.selectedFip?.FIP_NAME || "",
      UUID: state.auth.uuid || state.auth.deviceId || "",
    });

    if (res.RESULT_CODE !== "200") {
      return rejectWithValue(res.MESSAGE || "Failed to fetch identifiers");
    }
    return res;
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to fetch identifiers");
  }
});

export const discoverAccountsThunk = createAsyncThunk<
  AccountDiscoveryResponse,
  { fipId: string; fiTypes: string[] },
  { rejectValue: string; state: RootState }
>("account/discover", async (payload, { rejectWithValue, getState }) => {
  if (USE_MOCKS) return MOCK_ACCOUNTS as any;
  try {
    const state = getState();
    const sessionId = state.auth.sessionId || "";
    const mobileNumber = state.auth.mobileNumber;

    const requestPayload = {
      I_MOBILENUMBER: mobileNumber,
      I_BROWSER: API_DEFAULTS.BROWSER,
      I_SESSION: sessionId,
      I_USERID: mobileNumber,
      I_FIPID: payload.fipId,
      I_FIPNAME: state.fip.selectedFip?.FIP_NAME || "",
      UUID: state.auth.uuid || state.auth.deviceId || "",
      I_PAN: state.auth.panNumber || "",
      I_Email: state.auth.emailId || "",
      Identifiers: [
        {
          I_Flag: "MOBILE",
          DATA: mobileNumber,
          type: "STRONG"
        },
        ...(state.auth.panNumber ? [{
          I_Flag: "PAN",
          DATA: state.auth.panNumber,
          type: "STRONG"
        }] : []),
        ...(state.auth.emailId ? [{
          I_Flag: "EMAIL",
          DATA: state.auth.emailId,
          type: "STRONG"
        }] : [])
      ],
      I_Identifier: [
        {
          I_Flag: "MOBILE",
          DATA: mobileNumber,
          type: "STRONG"
        },
        ...(state.auth.panNumber ? [{
          I_Flag: "PAN",
          DATA: state.auth.panNumber,
          type: "STRONG"
        }] : []),
        ...(state.auth.emailId ? [{
          I_Flag: "EMAIL",
          DATA: state.auth.emailId,
          type: "STRONG"
        }] : [])
      ],
    };

    console.log("=== DISCOVER API DEBUG ===");
    console.log("1. Redux Auth State:", state.auth);
    console.log("2. Sending Discover Payload:", requestPayload);

    const res = await discoverAccounts(requestPayload);

    console.log("Discover API Response:", res);

    if (res.RESULT_CODE === "200") {
      const rawList = res.fip_DiscoverLinkedlist || res.fip_NewDiscoverelist || [];
      const allAccounts = rawList.map(acc => ({
        ACC_ID: acc.FIPACCREFNUM || acc.FIPACCNUM || Math.random().toString(),
        MASKED_ACC_NO: acc.FIPACCNUM || "XXXX",
        ACC_TYPE: acc.FIPACCTYPE || "SAVINGS",
        FI_TYPE: acc.FITYPE || "DEPOSIT",
        LINKED: !!acc.LINKEDDATE,
        FIPID: acc.FIPID || payload.fipId,
        FIPNAME: acc.FIPNAME || "",
        FIPACCREFNUM: acc.FIPACCREFNUM,
        LINKEDDATE: acc.LINKEDDATE,
        FIPACCLINKREF: acc.FIPACCLINKREF
      }));

      return { ACC_LIST: allAccounts };
    }

    return rejectWithValue(res.MESSAGE || "Account discovery failed");
  } catch (err: any) {
    return rejectWithValue(err.message || "Account discovery failed");
  }
});

export const linkAccountThunk = createAsyncThunk<
  LinkAccountResponse,
  { fipId: string; accounts: any[] },
  { rejectValue: string; state: RootState }
>("account/link", async (payload, { rejectWithValue, getState }) => {
  if (USE_MOCKS) return { RefNumber: "mock-link-456", RESULT_CODE: "200", RESULT_MESSAGE: "Success" } as any;
  try {
    const state = getState();
    const sessionId = state.auth.sessionId || "";
    const mobileNumber = state.auth.mobileNumber;
    const consentHandle = state.auth.consentHandle || state.consent.consentHandle || API_DEFAULTS.CONSENT_HANDLE;

    const accountsToLink = payload.accounts.map(acc => ({
      FIPACCNUM: acc.MASKED_ACC_NO || acc.FIPACCNUM,
      FIPACCREFNUM: acc.ACC_ID || acc.FIPACCREFNUM,
      FIPACCTYPE: acc.ACC_TYPE || acc.FIPACCTYPE,
      FIPTYPE: acc.FI_TYPE || acc.FITYPE,
      FIPID: payload.fipId
    }));

    console.log("Linking Payload:", {
      accountsToLink,
      mobileNumber,
      fipId: payload.fipId,
      fiuid: API_DEFAULTS.FIUID,
      consentHandle
    });

    const res = await linkAccount({
      I_MOBILENUMBER: mobileNumber,
      I_BROWSER: API_DEFAULTS.BROWSER,
      I_SESSION: sessionId,
      I_USERID: mobileNumber,
      I_FIPID: payload.fipId,
      I_ConsentHandle: "",
      I_FIUID: "",
      ACCOUNTS_TO_LINK: accountsToLink,
      UUID: state.auth.uuid || "",
    });

    if (res.RESULT_CODE !== "200") {
      return rejectWithValue(res.MESSAGE || "Account linking failed");
    }
    return res;
  } catch {
    return rejectWithValue("Account linking failed");
  }
});

export const verifyLinkAccountThunk = createAsyncThunk<
  VerifyLinkResponse,
  { otp: string; fipId: string; refNumber: string },
  { rejectValue: string; state: RootState }
>("account/verifyLink", async (payload, { rejectWithValue, getState }) => {
  if (USE_MOCKS) return { RESULT_CODE: "200", MESSAGE: "Success", fip_DiscoverLinkedlist: [] } as any;
  try {
    const state = getState();
    const sessionId = state.auth.sessionId || "";
    const mobileNumber = state.auth.mobileNumber;
    const consentHandle = state.auth.consentHandle || state.consent.consentHandle;
    if (!consentHandle) {
      return rejectWithValue("Consent handle is missing. Please restart the journey.");
    }

    const res = await verifyLinkAccount({
      I_MOBILENUMBER: mobileNumber,
      I_BROWSER: API_DEFAULTS.BROWSER,
      I_SESSION: sessionId,
      I_FIPID: payload.fipId,
      I_MOBOTP: payload.otp,
      I_USERID: mobileNumber,
      I_FIPACCREFNUM: payload.refNumber,
      UUID: state.auth.uuid || "",
    });

    console.log("Verify Link Payload sent:", {
      I_MOBILENUMBER: mobileNumber,
      I_FIPID: payload.fipId,
      I_FIPACCREFNUM: payload.refNumber,
      I_MOBOTP: payload.otp,
      sessionId,
      consentHandle
    });

    if (res.RESULT_CODE !== "200") {
      return rejectWithValue(res.MESSAGE || "OTP verification failed");
    }

    return res;
  } catch {
    return rejectWithValue("OTP verification failed");
  }
});

export const fetchDashboardDataThunk = createAsyncThunk<
  any,
  void,
  { rejectValue: string; state: RootState }
>("account/fetchDashboard", async (_, { rejectWithValue, getState }) => {
  if (USE_MOCKS) return { linkedAccounts: [], consents: [] };
  try {
    const state = getState();
    const mobileNumber = state.auth.mobileNumber || "";
    const sessionId = state.auth.sessionId || "";

    const res = await getConsentList({
      I_MOBILENUMBER: mobileNumber,
      I_SESSION: sessionId,
      I_USERID: mobileNumber
    });

    if (res.RESULT_CODE !== "200") {
      return rejectWithValue(res.MESSAGE || "Failed to fetch dashboard data");
    }

    // Assuming the response contains a list of consents and possibly accounts
    return res;
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to fetch dashboard data");
  }
});